<template>
  <div>
    <v-dialog v-model="loading" persistent width="300">
      <v-card dark>
        <v-card-text>
          Loading...
          <v-progress-linear indeterminate color="white" striped height="10" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container>
      <v-form v-model="valid">
        <h3 class="mb-3">Notifications Tester</h3>
        <v-text-field variant="underlined" label="MOC Id" max-length="14" v-model="mocId" :rules="requiredRules"></v-text-field>
        <v-checkbox dense v-model="isExternal" label="External"></v-checkbox>
        <v-btn :disabled="!mocId" class="mb-3" @click="getEmailVariables">See email variables for this MOC</v-btn>
        <v-select
          item-title="value"
          label="Email Type"
          variant="underlined"
          item-value="key"
          v-model="selectedEmailType"
          :items="emailTypes"
        ></v-select>
        <v-select
          item-title="value"
          label="Notifications"
          item-value="key"
          variant="underlined"
          v-model="selectedNotification"
          :items="notificationsComputed"
          return-object
        ></v-select>
        <v-btn :disabled="!valid" class="mb-3" @click="getNotifications">Get notification for this MOC</v-btn>
        <v-select
          item-title="value"
          item-value="key"
          dense
          :items="statuses"
          v-model="selectedStatus"
          v-if="selectedNotification && selectedNotification.tag"
          label="Status"
        ></v-select>
        <v-row dense>
          <v-col>
            <v-menu
              v-model="showStartDatePicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ props }">
                <v-text-field 
                  variant="underlined" 
                  :model-value="$filters.DateFilter(startDate)" 
                  label="From" 
                  prepend-icon="mdi-calendar" 
                  readonly 
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startDate" @update:modelValue="showStartDatePicker = false" hide-header></v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="showEndDatePicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  variant="underlined"
                  :model-value="$filters.DateFilter(endDate)"
                  label="To"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker v-model="endDate" @update:modelValue="showEndDatePicker = false" hide-header></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-btn :disabled="!valid" color="primary" class="mb-3" @click="seeLogs">See logs</v-btn>
      </v-form>
      <NotificationTesterLogView v-if="notificationLog" :notificationLog="notificationLog"></NotificationTesterLogView>
    </v-container>
    <v-dialog v-model="showVariablesDialog" max-width="600">
      <v-card>
        <v-container>
          <pre v-html="variables"></pre>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { NOTIFICATION_STATUS, NOTIFICATION_TAGS } from '@/common/notifications'
import MocService from '@/services/moc.service'
import NotificationTesterLogView from '@/components/developer/NotificationTesterLogView'
import moment from 'moment'

const notifications = NOTIFICATION_STATUS.map((n) => {
  return {
    key: n.value,
    value: n.name,
    hideReminders: n.hideReminders,
    hideInitials: n.hideInitials
  }
}).concat(
  NOTIFICATION_TAGS.map((n) => {
    return {
      key: n.key,
      value: n.value,
      hideReminders: n.hideReminders,
      hideInitials: n.hideInitials,
      tag: true
    }
  })
)

export default {
  name: 'NotificationTester',
  components: {
    NotificationTesterLogView
  },
  data() {
    return {
      loading: false,
      notifications,
      requiredRules: [(v) => (v && v.trim() ? true : false)],
      valid: false,
      mocId: '',
      selectedNotification: undefined,
      statuses: notifications.filter((n) => !n.tag),
      selectedStatus: undefined,
      selectedEmailType: 'initials',
      emailTypes: [
        {
          key: 'initials',
          value: 'Initials'
        },
        {
          key: 'reminders',
          value: 'Reminders'
        }
      ],
      notificationLog: undefined,
      showStartDatePicker: false,
      showEndDatePicker: false,
      startDate: new Date(),
      endDate: new Date(),
      variables: '',
      showVariablesDialog: false,
      isExternal: false
    }
  },
  computed: {
    notificationsComputed() {
      return this.notifications.filter((n) => (this.selectedEmailType === 'initials' ? !n.hideInitials : !n.hideReminders))
    },
    calculatedValue() {
      return this.startDate ? this.$options.filters.DateFilter(this.startDate) : ''
    }
  },
  methods: {
    getEmailVariables() {
      this.loading = true
      MocService.getEmailVariables(this.mocId, { isExternal: this.isExternal })
        .then((response) => {
          this.showVariablesDialog = true
          this.variables = response
        })
        .finally(() => (this.loading = false))
    },
    seeLogs() {
      this.loading = true
      MocService.getNotificationLogs(
        this.mocId,
        this.selectedNotification.key,
        this.selectedStatus,
        this.selectedNotification.tag,
        this.selectedEmailType === 'reminders',
        { start: moment(this.startDate).format('YYYY-MM-DD'), end: moment(this.endDate).format('YYYY-MM-DD') }
      )
        .then((response) => {
          this.notificationLog = response ? response[0] : undefined
        })
        .finally(() => (this.loading = false))
    },
    getNotifications() {
      this.loading = true
      MocService.getNotifications(
        this.mocId,
        this.selectedNotification.key,
        this.selectedStatus,
        this.selectedNotification.tag,
        this.selectedEmailType === 'reminders'
      ).finally(() => (this.loading = false))
    }
  }
}
</script>

<style></style>
